import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import DataTable from 'react-data-table-component';
import {
    Calculator, Clock, Plus, Printer, Check, X, Loader2, AlertCircle,
    Banknote, FileText, Settings, Users, Trash2, Eye, Wallet
} from 'lucide-react';
import { printLiquidacion, formatClp } from '../utils/printLiquidacion';

const TABS = [
    { id: 'liquidaciones', label: 'Liquidaciones', icon: FileText },
    { id: 'extras', label: 'Horas extras', icon: Clock },
    { id: 'movimientos', label: 'Haberes y descuentos', icon: Wallet },
    { id: 'parametros', label: 'Parámetros', icon: Settings },
];

const ESTADO_LIQ = {
    borrador: { label: 'Borrador', bg: 'rgba(245, 158, 11, 0.12)', color: '#d97706' },
    emitida: { label: 'Emitida', bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb' },
    pagada: { label: 'Pagada', bg: 'rgba(16, 185, 129, 0.12)', color: '#059669' },
    anulada: { label: 'Anulada', bg: 'rgba(239, 68, 68, 0.12)', color: '#dc2626' },
};

const ESTADO_HE = {
    pendiente: { label: 'Pendiente', bg: 'rgba(245, 158, 11, 0.12)', color: '#d97706' },
    aprobada: { label: 'Aprobada', bg: 'rgba(16, 185, 129, 0.12)', color: '#059669' },
    rechazada: { label: 'Rechazada', bg: 'rgba(239, 68, 68, 0.12)', color: '#dc2626' },
    liquidada: { label: 'Liquidada', bg: 'rgba(99, 102, 241, 0.12)', color: '#4f46e5' },
};

function currentPeriodo() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Badge({ map, value }) {
    const s = map[value] || { label: value, bg: '#f5f5f4', color: '#57534e' };
    return (
        <span style={{
            background: s.bg, color: s.color, padding: '0.25rem 0.7rem',
            borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize'
        }}>{s.label}</span>
    );
}

const tableStyles = {
    headRow: {
        style: {
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid var(--border-color)',
            fontWeight: '600',
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            fontSize: '0.75rem'
        }
    },
    cells: { style: { paddingLeft: '16px', paddingRight: '16px' } }
};

export default function LiquidacionesPage() {
    const [tab, setTab] = useState('liquidaciones');
    const [periodo, setPeriodo] = useState(currentPeriodo);
    const [colaboradores, setColaboradores] = useState([]);
    const [liquidaciones, setLiquidaciones] = useState([]);
    const [horasExtras, setHorasExtras] = useState([]);
    const [movimientos, setMovimientos] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [parametros, setParametros] = useState([]);
    const [paramForm, setParamForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [preview, setPreview] = useState(null);
    const [heModal, setHeModal] = useState(false);
    const [movModal, setMovModal] = useState(false);
    const [heForm, setHeForm] = useState({ colaborador_id: '', fecha: '', horas: '', recargo_pct: 50, motivo: '', estado: 'aprobada' });
    const [movForm, setMovForm] = useState({ colaborador_id: '', tipo: 'haber_imponible', concepto: '', monto: '', notas: '' });

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
    }), [token]);

    const fetchAll = useCallback(async () => {
        setError(null);
        try {
            const [colabRes, liqRes, heRes, movRes, sumRes, parRes] = await Promise.all([
                fetch(`${API_URL}/api/colaboradores`, { headers }),
                fetch(`${API_URL}/api/liquidaciones?periodo=${periodo}`, { headers }),
                fetch(`${API_URL}/api/horas-extras?periodo=${periodo}`, { headers }),
                fetch(`${API_URL}/api/liquidaciones/movimientos?periodo=${periodo}`, { headers }),
                fetch(`${API_URL}/api/liquidaciones/resumen?periodo=${periodo}`, { headers }),
                fetch(`${API_URL}/api/liquidaciones/parametros`, { headers }),
            ]);
            const readJson = async (res) => {
                const ct = res.headers.get('content-type') || '';
                if (!ct.includes('application/json')) return {};
                try { return await res.json(); } catch { return {}; }
            };
            if (!liqRes.ok) {
                const data = await readJson(liqRes);
                if (liqRes.status === 403) {
                    throw new Error(data.error || 'No tiene permiso para ver liquidaciones. Asigne liquidaciones_ver al rol o vuelva a iniciar sesión.');
                }
                if (liqRes.status === 404) {
                    throw new Error(data.error || 'El backend no tiene la ruta de liquidaciones. Reinicie la API o vuelva a desplegar.');
                }
                throw new Error(data.error || `Error al cargar liquidaciones (HTTP ${liqRes.status})`);
            }
            const colabData = colabRes.ok ? await colabRes.json() : [];
            const liqData = await liqRes.json();
            setColaboradores(Array.isArray(colabData) ? colabData : []);
            setLiquidaciones(Array.isArray(liqData) ? liqData : []);
            setHorasExtras(heRes.ok ? await heRes.json() : []);
            setMovimientos(movRes.ok ? await movRes.json() : []);
            setResumen(sumRes.ok ? await sumRes.json() : null);
            if (parRes.ok) {
                const data = await parRes.json();
                setParametros(data.parametros || []);
                const form = {};
                (data.parametros || []).forEach((p) => { form[p.clave] = p.valor; });
                setParamForm(form);
            }
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [headers, periodo]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const api = async (url, options = {}) => {
        const res = await fetch(url, { headers, ...options });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Error en la operación');
        return data;
    };

    const generarUno = async (colaboradorId, emitir = false) => {
        setSaving(true);
        try {
            await api(`${API_URL}/api/liquidaciones`, {
                method: 'POST',
                body: JSON.stringify({
                    colaborador_id: colaboradorId,
                    periodo,
                    estado: emitir ? 'emitida' : 'borrador',
                }),
            });
            setPreview(null);
            await fetchAll();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const generarTodos = async () => {
        if (!confirm(`¿Generar borradores de liquidación para todos los colaboradores activos en ${periodo}?`)) return;
        setSaving(true);
        try {
            const data = await api(`${API_URL}/api/liquidaciones/generar-periodo`, {
                method: 'POST',
                body: JSON.stringify({ periodo, estado: 'borrador' }),
            });
            alert(`Se generaron ${data.total} liquidaciones.${data.skipped?.length ? ` Omitidas: ${data.skipped.length}` : ''}`);
            await fetchAll();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const verPreview = async (colaboradorId) => {
        try {
            const data = await api(`${API_URL}/api/liquidaciones/previsualizar?periodo=${periodo}&colaborador_id=${colaboradorId}`);
            setPreview(data);
        } catch (err) {
            alert(err.message);
        }
    };

    const accionLiq = async (id, action, confirmMsg) => {
        if (confirmMsg && !confirm(confirmMsg)) return;
        try {
            await api(`${API_URL}/api/liquidaciones/${id}/${action}`, { method: 'POST' });
            await fetchAll();
        } catch (err) {
            alert(err.message);
        }
    };

    const submitHe = async (e) => {
        e.preventDefault();
        try {
            await api(`${API_URL}/api/horas-extras`, {
                method: 'POST',
                body: JSON.stringify(heForm),
            });
            setHeModal(false);
            setHeForm({ colaborador_id: '', fecha: '', horas: '', recargo_pct: 50, motivo: '', estado: 'aprobada' });
            await fetchAll();
        } catch (err) {
            alert(err.message);
        }
    };

    const submitMov = async (e) => {
        e.preventDefault();
        try {
            await api(`${API_URL}/api/liquidaciones/movimientos`, {
                method: 'POST',
                body: JSON.stringify({ ...movForm, periodo }),
            });
            setMovModal(false);
            setMovForm({ colaborador_id: '', tipo: 'haber_imponible', concepto: '', monto: '', notas: '' });
            await fetchAll();
        } catch (err) {
            alert(err.message);
        }
    };

    const saveParams = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api(`${API_URL}/api/liquidaciones/parametros`, {
                method: 'PUT',
                body: JSON.stringify({ parametros: paramForm }),
            });
            alert('Parámetros guardados. Se usarán en las próximas liquidaciones.');
            await fetchAll();
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const liqColumns = [
        {
            name: 'Colaborador',
            selector: (row) => row.colaborador_nombre,
            sortable: true,
            grow: 2,
            cell: (row) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{row.colaborador_nombre}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{row.colaborador_rut || 'Sin RUT'}</div>
                </div>
            ),
        },
        { name: 'Días', selector: (row) => row.dias_trabajados, width: '80px' },
        { name: 'HE', selector: (row) => row.horas_extras, width: '80px' },
        { name: 'Haberes', selector: (row) => Number(row.total_haberes), sortable: true, cell: (row) => formatClp(row.total_haberes) },
        { name: 'Descuentos', selector: (row) => Number(row.total_descuentos), cell: (row) => formatClp(row.total_descuentos) },
        { name: 'Líquido', selector: (row) => Number(row.liquido), sortable: true, cell: (row) => <strong>{formatClp(row.liquido)}</strong> },
        { name: 'Estado', selector: (row) => row.estado, cell: (row) => <Badge map={ESTADO_LIQ} value={row.estado} /> },
        {
            name: 'Acciones',
            width: '220px',
            cell: (row) => (
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button type="button" title="Imprimir" onClick={() => printLiquidacion(row)} style={iconBtn('#4f46e5')}>
                        <Printer size={15} />
                    </button>
                    {row.estado === 'borrador' && (
                        <button type="button" title="Emitir" onClick={() => accionLiq(row.id, 'emitir', '¿Emitir esta liquidación? Quedará bloqueada.')} style={iconBtn('#059669')}>
                            <Check size={15} />
                        </button>
                    )}
                    {row.estado === 'emitida' && (
                        <button type="button" title="Marcar pagada" onClick={() => accionLiq(row.id, 'pagar', '¿Marcar esta liquidación como pagada?')} style={iconBtn('#2563eb')}>
                            <Banknote size={15} />
                        </button>
                    )}
                    {(row.estado === 'borrador' || row.estado === 'emitida') && (
                        <button type="button" title="Anular" onClick={() => accionLiq(row.id, 'anular', '¿Anular esta liquidación?')} style={iconBtn('#dc2626')}>
                            <Trash2 size={15} />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const heColumns = [
        { name: 'Fecha', selector: (row) => String(row.fecha).slice(0, 10), sortable: true, width: '120px' },
        { name: 'Colaborador', selector: (row) => row.colaborador_nombre, sortable: true, grow: 2 },
        { name: 'Horas', selector: (row) => row.horas, width: '90px' },
        { name: 'Recargo', selector: (row) => `${row.recargo_pct}%`, width: '90px' },
        { name: 'Monto est.', selector: (row) => Number(row.monto_estimado), cell: (row) => formatClp(row.monto_estimado) },
        { name: 'Motivo', selector: (row) => row.motivo || '—' },
        { name: 'Estado', selector: (row) => row.estado, cell: (row) => <Badge map={ESTADO_HE} value={row.estado} /> },
        {
            name: '',
            width: '160px',
            cell: (row) => row.estado === 'liquidada' ? null : (
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                    {row.estado === 'pendiente' && (
                        <button type="button" title="Aprobar" onClick={() => api(`${API_URL}/api/horas-extras/${row.id}/estado`, { method: 'POST', body: JSON.stringify({ estado: 'aprobada' }) }).then(fetchAll).catch((e) => alert(e.message))} style={iconBtn('#059669')}>
                            <Check size={15} />
                        </button>
                    )}
                    <button type="button" title="Eliminar" onClick={() => { if (confirm('¿Eliminar este registro?')) api(`${API_URL}/api/horas-extras/${row.id}`, { method: 'DELETE' }).then(fetchAll).catch((e) => alert(e.message)); }} style={iconBtn('#dc2626')}>
                        <Trash2 size={15} />
                    </button>
                </div>
            ),
        },
    ];

    const movColumns = [
        { name: 'Colaborador', selector: (row) => row.colaborador_nombre, sortable: true, grow: 2 },
        { name: 'Tipo', selector: (row) => row.tipo, cell: (row) => tipoMovLabel(row.tipo) },
        { name: 'Concepto', selector: (row) => row.concepto },
        { name: 'Monto', selector: (row) => Number(row.monto), cell: (row) => formatClp(row.monto) },
        {
            name: '',
            width: '80px',
            cell: (row) => !row.liquidacion_id && (
                <button type="button" title="Eliminar" onClick={() => { if (confirm('¿Eliminar este movimiento?')) api(`${API_URL}/api/liquidaciones/movimientos/${row.id}`, { method: 'DELETE' }).then(fetchAll).catch((e) => alert(e.message)); }} style={iconBtn('#dc2626')}>
                    <Trash2 size={15} />
                </button>
            ),
        },
    ];

    const sinLiquidacion = colaboradores.filter((c) =>
        c.estado === 'activo' && !liquidaciones.some((l) => l.colaborador_id === c.id && l.estado !== 'anulada')
    );

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                <Loader2 size={48} className="spinning" style={{ color: 'var(--primary-color)' }} />
            </div>
        );
    }

    return (
        <div className="page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <Calculator size={28} />
                        Liquidaciones de sueldo
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                        Calcula haberes, descuentos legales, horas extras y genera la liquidación del periodo.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label className="form-group" style={{ margin: 0 }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Periodo</span>
                        <input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
                    </label>
                    {tab === 'liquidaciones' && (
                        <button type="button" className="btn-primary" onClick={generarTodos} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {saving ? <Loader2 size={16} className="spinning" /> : <Users size={16} />}
                            Generar periodo
                        </button>
                    )}
                    {tab === 'extras' && (
                        <button type="button" className="btn-primary" onClick={() => setHeModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Plus size={16} /> Registrar horas extras
                        </button>
                    )}
                    {tab === 'movimientos' && (
                        <button type="button" className="btn-primary" onClick={() => setMovModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Plus size={16} /> Agregar movimiento
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="report-card" style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', color: 'var(--danger-color)' }}>
                    <AlertCircle size={18} /> {error}
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                <Stat label="Activos" value={resumen?.colaboradores_activos ?? colaboradores.filter((c) => c.estado === 'activo').length} />
                <Stat label="Liquidaciones" value={resumen?.liquidaciones?.total || 0} />
                <Stat label="Líquido del mes" value={formatClp(resumen?.liquidaciones?.total_liquido || 0)} />
                <Stat label="HE pendientes" value={resumen?.horas_extras?.pendientes || 0} />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={active ? 'btn-primary' : ''}
                            style={active ? { display: 'flex', alignItems: 'center', gap: '0.4rem' } : {
                                display: 'flex', alignItems: 'center', gap: '0.4rem',
                                padding: '0.6rem 1rem', borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--border-color)', background: 'white', cursor: 'pointer'
                            }}
                        >
                            <Icon size={16} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === 'liquidaciones' && (
                <>
                    {sinLiquidacion.length > 0 && (
                        <div className="report-card" style={{ marginBottom: '1rem' }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.75rem' }}>
                                Sin liquidación en {periodo} ({sinLiquidacion.length})
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {sinLiquidacion.map((c) => (
                                    <div key={c.id} style={{
                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        border: '1px solid var(--border-color)', borderRadius: '9999px', padding: '0.35rem 0.6rem 0.35rem 0.9rem'
                                    }}>
                                        <span>{c.name}</span>
                                        <button type="button" title="Previsualizar" onClick={() => verPreview(c.id)} style={iconBtn('#4f46e5', true)}>
                                            <Eye size={14} />
                                        </button>
                                        <button type="button" title="Generar borrador" onClick={() => generarUno(c.id, false)} style={iconBtn('#059669', true)}>
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <DataTable
                            columns={liqColumns}
                            data={liquidaciones}
                            pagination
                            highlightOnHover
                            responsive
                            customStyles={tableStyles}
                            noDataComponent={<Empty text="No hay liquidaciones en este periodo" />}
                        />
                    </div>
                </>
            )}

            {tab === 'extras' && (
                <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <DataTable
                        columns={heColumns}
                        data={horasExtras}
                        pagination
                        highlightOnHover
                        responsive
                        customStyles={tableStyles}
                        noDataComponent={<Empty text="No hay horas extras en este periodo" />}
                    />
                </div>
            )}

            {tab === 'movimientos' && (
                <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <DataTable
                        columns={movColumns}
                        data={movimientos}
                        pagination
                        highlightOnHover
                        responsive
                        customStyles={tableStyles}
                        noDataComponent={<Empty text="No hay haberes ni descuentos extra en este periodo" />}
                    />
                </div>
            )}

            {tab === 'parametros' && (
                <form onSubmit={saveParams} className="report-card">
                    <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
                        Valores legales usados al calcular. Actualice UTM y UF cada mes antes de emitir.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                        {parametros.map((p) => (
                            <div className="form-group" key={p.clave}>
                                <label>{p.clave}</label>
                                <input
                                    value={paramForm[p.clave] ?? ''}
                                    onChange={(e) => setParamForm({ ...paramForm, [p.clave]: e.target.value })}
                                />
                                {p.descripcion && (
                                    <small style={{ color: 'var(--text-secondary)' }}>{p.descripcion}</small>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="form-actions" style={{ marginTop: '1.25rem' }}>
                        <button type="submit" className="btn-primary" disabled={saving}>
                            {saving ? 'Guardando…' : 'Guardar parámetros'}
                        </button>
                    </div>
                </form>
            )}

            {preview && (
                <div className="modal-overlay" onClick={() => setPreview(null)}>
                    <div className="modal-content" style={{ maxWidth: '720px' }} onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setPreview(null)} aria-label="Cerrar"><X size={20} /></button>
                        <h2 className="modal-title">Previsualización — {preview.colaborador_nombre}</h2>
                        <p className="text-muted" style={{ marginTop: 0 }}>
                            {preview.periodo} · {preview.dias_trabajados} días · {preview.horas_extras} h extras · valor hora {formatClp(preview.valor_hora)}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div>
                                <strong>Haberes</strong>
                                {preview.haberes.map((h) => (
                                    <div key={h.codigo} style={lineRow}>
                                        <span>{h.nombre}</span><span>{formatClp(h.monto)}</span>
                                    </div>
                                ))}
                                <div style={{ ...lineRow, fontWeight: 700 }}>
                                    <span>Total</span><span>{formatClp(preview.total_haberes)}</span>
                                </div>
                            </div>
                            <div>
                                <strong>Descuentos</strong>
                                {preview.descuentos.length === 0 && <p className="text-muted">Sin descuentos</p>}
                                {preview.descuentos.map((d) => (
                                    <div key={d.codigo} style={lineRow}>
                                        <span>{d.nombre}</span><span>{formatClp(d.monto)}</span>
                                    </div>
                                ))}
                                <div style={{ ...lineRow, fontWeight: 700 }}>
                                    <span>Total</span><span>{formatClp(preview.total_descuentos)}</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: '#f5f5f4', borderRadius: '8px', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Líquido a pagar</span>
                            <strong style={{ fontSize: '1.15rem' }}>{formatClp(preview.liquido)}</strong>
                        </div>
                        <div className="form-actions" style={{ marginTop: '1.25rem' }}>
                            <button type="button" onClick={() => printLiquidacion(preview)} style={secondaryBtn}>
                                <Printer size={16} /> Imprimir
                            </button>
                            <button type="button" className="btn-primary" disabled={saving} onClick={() => generarUno(preview.colaborador_id, false)}>
                                Guardar borrador
                            </button>
                            <button type="button" className="btn-primary" disabled={saving} onClick={() => generarUno(preview.colaborador_id, true)}>
                                Emitir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {heModal && (
                <div className="modal-overlay" onClick={() => setHeModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setHeModal(false)} aria-label="Cerrar"><X size={20} /></button>
                        <h2 className="modal-title">Registrar horas extras</h2>
                        <form onSubmit={submitHe}>
                            <div className="form-group">
                                <label>Colaborador *</label>
                                <select required value={heForm.colaborador_id} onChange={(e) => setHeForm({ ...heForm, colaborador_id: e.target.value })} style={selectStyle}>
                                    <option value="">Seleccionar</option>
                                    {colaboradores.filter((c) => c.estado === 'activo').map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="form-group">
                                    <label>Fecha *</label>
                                    <input required type="date" value={heForm.fecha} onChange={(e) => setHeForm({ ...heForm, fecha: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Horas *</label>
                                    <input required type="number" min="0.25" step="0.25" value={heForm.horas} onChange={(e) => setHeForm({ ...heForm, horas: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Recargo</label>
                                    <select value={heForm.recargo_pct} onChange={(e) => setHeForm({ ...heForm, recargo_pct: e.target.value })} style={selectStyle}>
                                        <option value={50}>50% (ordinario)</option>
                                        <option value={100}>100% (festivo / especial)</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Estado</label>
                                    <select value={heForm.estado} onChange={(e) => setHeForm({ ...heForm, estado: e.target.value })} style={selectStyle}>
                                        <option value="pendiente">Pendiente</option>
                                        <option value="aprobada">Aprobada</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Motivo</label>
                                <input value={heForm.motivo} onChange={(e) => setHeForm({ ...heForm, motivo: e.target.value })} placeholder="Cierre de local, reemplazo…" />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setHeModal(false)} style={secondaryBtn}>Cancelar</button>
                                <button type="submit" className="btn-primary">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {movModal && (
                <div className="modal-overlay" onClick={() => setMovModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setMovModal(false)} aria-label="Cerrar"><X size={20} /></button>
                        <h2 className="modal-title">Haber o descuento del periodo</h2>
                        <form onSubmit={submitMov}>
                            <div className="form-group">
                                <label>Colaborador *</label>
                                <select required value={movForm.colaborador_id} onChange={(e) => setMovForm({ ...movForm, colaborador_id: e.target.value })} style={selectStyle}>
                                    <option value="">Seleccionar</option>
                                    {colaboradores.filter((c) => c.estado === 'activo').map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Tipo *</label>
                                <select required value={movForm.tipo} onChange={(e) => setMovForm({ ...movForm, tipo: e.target.value })} style={selectStyle}>
                                    <option value="haber_imponible">Haber imponible (bono, trato)</option>
                                    <option value="haber_no_imponible">Haber no imponible</option>
                                    <option value="descuento">Descuento (anticipo, préstamo)</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Concepto *</label>
                                <input required value={movForm.concepto} onChange={(e) => setMovForm({ ...movForm, concepto: e.target.value })} placeholder="Anticipo, bono producción…" />
                            </div>
                            <div className="form-group">
                                <label>Monto *</label>
                                <input required type="number" min="1" step="1" value={movForm.monto} onChange={(e) => setMovForm({ ...movForm, monto: e.target.value })} />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setMovModal(false)} style={secondaryBtn}>Cancelar</button>
                                <button type="submit" className="btn-primary">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="report-card" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--primary-color)' }}>{value}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{label}</div>
        </div>
    );
}

function Empty({ text }) {
    return (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <Calculator size={40} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
            <p>{text}</p>
        </div>
    );
}

function tipoMovLabel(tipo) {
    if (tipo === 'haber_imponible') return 'Haber imponible';
    if (tipo === 'haber_no_imponible') return 'Haber no imponible';
    if (tipo === 'descuento') return 'Descuento';
    return tipo;
}

function iconBtn(color, compact = false) {
    return {
        background: `${color}18`,
        border: 'none',
        borderRadius: '8px',
        padding: compact ? '0.3rem' : '0.45rem',
        cursor: 'pointer',
        color,
        display: 'inline-flex',
    };
}

const selectStyle = {
    width: '100%',
    padding: '0.625rem 0.875rem',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-input)',
    fontSize: '0.875rem',
};

const secondaryBtn = {
    padding: '0.75rem 1.5rem',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-color)',
    background: 'white',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
};

const lineRow = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '0.35rem 0',
    borderBottom: '1px solid #f5f5f4',
    fontSize: '0.875rem',
};
